//src/services/api.js
import axios from 'axios';
import { API_URL } from '../config/constants';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true  // Add this line
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log the request configuration
    console.log('API Request:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      hasToken: !!token
    });
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses
    console.log('API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    return response;
  },
  (error) => {
    // Log failed responses
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const hubspotAPI = {
  createContact: async (email, source) => {
    try {
      const response = await api.post('/hubspot/contact', { 
        email, 
        source,
        // Add more context about where the lead came from
        context: {
          page: 'Email Verification',
          action: 'Get Started Button',
          timestamp: new Date().toISOString()
        }
      });
      return response.data;
    } catch (error) {
      // Better error handling
      throw new Error(error.response?.data?.message || 'Failed to submit contact information');
    }
  }
};

// Auth related endpoints
// Auth related endpoints - Updated version
export const authAPI = {
  googleLogin: (flowType) => `${API_URL}/auth/google-login${flowType ? `?flowType=${flowType}` : ''}`,
  verifyToken: () => api.get('/auth/verify-token'),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  completeSignup: async (signupData) => {
    try {
      const response = await api.post('/auth/complete-signup', signupData);
      return response.data;
    } catch (error) {
      console.error('Complete signup error:', error);
      throw error;
    }
  },
  createDefaultSubscription: async () => {
    try {
      console.log('Creating default subscription for new user');
      const response = await api.post('/subscriptions/create-default', {
        seats: 1,
        planType: 'free'
      });
      console.log('Default subscription created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to create default subscription:', error);
      throw error;
    }
  }
};

// Email and analytics related endpoints
// Enhanced email API service for better verification support
export const emailAPI = {
  // Analytics endpoints
  getMetrics: (organizationId) => {
    if (!organizationId) {
      return Promise.reject(new Error('Organization ID is required'));
    }
    return api.get(`/dashboard/analytics`, {
      params: { organization_id: organizationId }
    });
  },
  getSecurityStats: () => api.get('/dashboard/security'),
  
  // Email verification endpoints with enhanced error handling
  verifyEmail: async (verificationData) => {
    try {
      console.log('Verifying email:', verificationData);
      
      const normalizedData = typeof verificationData === 'object' 
        ? verificationData 
        : { scode: verificationData, recipientEmail: arguments[1] };
      
      if (!normalizedData.scode) {
        throw new Error('Missing required scode parameter');
      }

      const edgeFunctionUrl = 'https://fnqcrzgslrpskevhgxpq.supabase.co/functions/v1/email-verification';
      
      console.log('Attempting edge function verification:', normalizedData);
      const edgeFunctionResponse = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZucWNyemdzbHJwc2tldmhneHBxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzgxNzc5MTQsImV4cCI6MjA1Mzc1MzkxNH0.QoopAC3nQ7ES2QDVRXi3V8h8OJI5DqMZELZbAI6NqHw'
        },
        credentials: 'include',
        body: JSON.stringify(normalizedData)
      });

      if (!edgeFunctionResponse.ok) {
        // Handle non-OK responses
        const errorText = await edgeFunctionResponse.text();
        console.warn('Edge function HTTP error:', edgeFunctionResponse.status, errorText);
        throw new Error(errorText || 'Verification failed');
      }

      const edgeFunctionData = await edgeFunctionResponse.json();
      console.log('Edge function verification response:', edgeFunctionData);
      
      if (edgeFunctionData.success) {
        console.log('Email verified successfully via edge function');
        // Format response to match expected format
        return { data: edgeFunctionData };
      } else {
        // If the function returned a properly formatted error
        console.warn('Edge function verification failed:', edgeFunctionData.message);
        const error = new Error(edgeFunctionData.message || 'Verification failed');
        error.response = { 
          status: edgeFunctionResponse.status,
          data: edgeFunctionData
        };
        throw error;
      }
    } catch (error) {
      console.error('Email verification error:', error);
      
      // Enhance error message based on response status
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 403) {
          error.userMessage = 'The email address provided does not match any recipient of this message.';
        } else if (status === 404) {
          error.userMessage = 'Invalid verification code. Please check and try again.';
        } else if (data?.message) {
          error.userMessage = data.message;
        }
      }
      
      throw error;
    }
  },
  
  // For status checks without triggering a verification count update
  getVerificationStatus: async (scode) => {
    try {
      return await api.get(`/emails/verify/status/${scode}`);
    } catch (error) {
      console.error('Error checking verification status:', error);
      throw error;
    }
  },
  
  // Get thread history for an email
  getThreadHistory: async (scode) => {
    try {
      return await api.get(`/emails/thread/${scode}`);
    } catch (error) {
      console.error('Error fetching thread history:', error);
      // Return empty thread history on error
      return { data: { success: true, data: { hasReplies: false, replies: [] } } };
    }
  },
  
  // Email management endpoints
  getAllEmails: () => api.get('/dashboard/emails'),
  getEmailStats: () => api.get('/dashboard/stats'),
  
  // Timeline data for analytics
  getTimelineData: () => api.get('/dashboard/analytics'),
  getTopDomains: () => api.get('/dashboard/analytics'),
  getVerificationRates: () => api.get('/dashboard/analytics')
};

// Dashboard related endpoints
// Update the dashboardAPI section in api.js
export const dashboardAPI = {
  // Basic dashboard stats
  getStats: async (organizationId) => {
    if (!organizationId) {
      return Promise.reject(new Error('Organization ID is required'));
    }
    try {
      console.log('[dashboardAPI] Requesting stats for org:', organizationId);
      const response = await api.get(`/dashboard/stats`, { 
        params: { organization_id: organizationId }
      });
      console.log('[dashboardAPI] Raw response:', response);

      // The server returns something like:
      // {
      //   success: true,
      //   data: {
      //     organization: { ... },
      //     userStats: { ... }
      //   }
      // }
      // So let's pull out the nested data safely:
      const { success, data } = response.data;
      const { organization = {}, userStats = {} } = data || {};

      return {
        success: !!success,
        data: {
          organization,
          userStats: {
            totalEmails: userStats.total_emails || 0,
            verifiedEmails: userStats.verified_emails || 0,
            dailyEmails: userStats.daily_emails || 0,
            verificationRate: userStats.verification_rate || 0,
            uniqueRecipientsCount: userStats.unique_recipients_count || 0,
            uniqueVerifiedRecipientsCount: userStats.unique_verified_recipients_count || 0,
            avgVerificationTime: userStats.avg_verification_time || 0,
            timelineData: userStats.timeline_data || {}
          },
          lastUpdated: new Date()
        }
      };
    } catch (error) {
      console.error('[dashboardAPI] Error fetching stats:', error);
      throw error;
    }
  },

  // Detailed metrics
  getAnalytics: async (organizationId) => {
    if (!organizationId) {
      return Promise.reject(new Error('Organization ID is required'));
    }
    try {
      const response = await api.get(`/dashboard/analytics`, {
        params: { organization_id: organizationId }
      });
      
      // Return the data directly since it's already in the correct format
      return response.data;
    } catch (error) {
      console.error('[dashboardAPI] Error fetching analytics:', error);
      throw error;
    }},
    
    getSecurityMetrics: async (organizationId) => {
      if (!organizationId) {
        return Promise.reject(new Error('Organization ID is required'));
      }
      try {
        console.log('[dashboardAPI] Requesting security metrics for org:', organizationId);
        const response = await api.get('/dashboard/security', {
          params: { organization_id: organizationId }
        });
        console.log('[dashboardAPI] Security metrics response:', response);
        return response;
      } catch (error) {
        console.error('[dashboardAPI] Error fetching security metrics:', error);
        throw error;
      }
    },
};


// User management endpoints
export const userAPI = {
  // Profile management
  updateProfile: (data) => {
    const payload = {
      ...data,
      timezone: data.timezone || 'America/Los_Angeles'
    };
    return api.put('/users/profile', payload);
  },
  getProfile: () => api.get('/users/profile'),
  updateProfileName: (data) => api.put('/users/profile/name', {
    firstName: data.firstName,
    lastName: data.lastName
  }),
  
  // Settings management
  updateSettings: (data) => api.put('/users/settings', data),
  getSettings: () => api.get('/users/settings'),

  // Additional profile fields
  updateUserInfo: (data) => {
    const payload = {
      phoneNumber: data.phoneNumber,
      jobTitle: data.jobTitle,
      department: data.department,
      timezone: data.timezone || 'America/Los_Angeles',
      emailNotifications: data.emailNotifications
    };
    return api.put('/users/info', payload);
  }
};


// Admin related endpoints
export const adminAPI = {
  getWorkspaceStatus: (domain) => api.get(`/admin/workspace/status/${domain}`),
  setupWorkspace: (data) => api.post('/admin/workspace/setup', data),
  syncWorkspace: (domain) => api.post(`/admin/workspace/sync/${domain}`),
  getComplianceRule: (domain) => api.get(`/admin/workspace/compliance-rule/${domain}`)
};

export const organizationAPI = {
  // Organization Details
  getDetails: (organizationId) => 
    api.get(`/organizations/${organizationId}`),
  
  update: (organizationId, data) => 
    api.put(`/organizations/${organizationId}`, data),
  
  // Members
  getMembers: (organizationId) => 
    api.get(`/organizations/${organizationId}/members`),
  
  // Stats & Analytics  
  getStats: (organizationId) => 
    api.get(`/dashboard/stats?organization_id=${organizationId}`),
    
  getAnalytics: (organizationId) => 
    api.get(`/dashboard/analytics?organization_id=${organizationId}`),
    
  getDashboardStats: (organizationId) => 
    api.get(`/dashboard/stats?organization_id=${organizationId}`),

  // Departments
  getDepartments: (organizationId) => 
    api.get(`/organizations/${organizationId}/departments`),
    
  createDepartment: (organizationId, data) => 
    api.post(`/organizations/${organizationId}/departments`, data),
    
  updateDepartment: (organizationId, departmentId, data) => 
    api.put(`/organizations/${organizationId}/departments/${departmentId}`, data),
    
  deleteDepartment: (organizationId, departmentId) => 
    api.delete(`/organizations/${organizationId}/departments/${departmentId}`),

  // Department Members
  assignToDepartment: (organizationId, departmentId, userId) => 
    api.post(`/organizations/${organizationId}/departments/${departmentId}/members`, { userId }),
    
  removeDepartmentMember: (organizationId, departmentId, userId) =>
    api.delete(`/organizations/${organizationId}/departments/${departmentId}/members/${userId}`),

  // Invitations
  inviteMember: (organizationId, data) => 
    api.post(`/organizations/${organizationId}/invitations`, data),
    
  cancelInvitation: (organizationId, invitationId) => 
    api.delete(`/organizations/${organizationId}/invitations/${invitationId}`),
    
  getInvitations: (organizationId) => 
    api.get(`/organizations/${organizationId}/invitations`),

  // Settings
  getSettings: (organizationId) => 
    api.get(`/organizations/${organizationId}/settings`)
};

// Add to api.js

export const subscriptionAPI = {
  // Get available plans
  getPlans: () => api.get('/subscription/plans'),

  // Create subscription
  createSubscription: (organizationId, data) => 
    api.post(`/organizations/${organizationId}/subscribe`, {
      ...data,
      idempotencyKey: `sub_create_${Date.now()}_${organizationId}`
    }),

  // Update subscription - Changed from PATCH to PUT
  updateSubscription: async (organizationId, subscriptionId, data) =>
    api.put(`/subscription/organizations/${organizationId}/subscriptions/${subscriptionId}`, {
      ...data,
      idempotencyKey: `sub_update_${Date.now()}_${subscriptionId}`
    }),

  // Cancel subscription – update similarly if needed
  cancelSubscription: async (organizationId, subscriptionId, cancelImmediately = false) =>
    api.post(`/subscription/organizations/${organizationId}/subscriptions/${subscriptionId}/cancel`, 
      { cancelImmediately }
    ),

  // Get billing history
  getBillingHistory: (organizationId) => 
    api.get(`/organizations/${organizationId}/billing`),

  // Get usage stats
  getUsageStats: (organizationId, startDate, endDate) =>
    api.get(`/organizations/${organizationId}/usage`, {
      params: { startDate, endDate }
    }),

    

  // Seat management
  assignSeat: (organizationId, userId) =>
    api.post(`/organizations/${organizationId}/seats/assign`, { userId }),

  unassignSeat: (organizationId, userId) =>
    api.post(`/organizations/${organizationId}/seats/unassign`, { userId }),

  reallocateSeats: (organizationId, userIds) =>
    api.post(`/organizations/${organizationId}/seats/reallocate`, { userIds }),
  // In your subscriptionAPI object (api.js)
addSeatsWithPayment: async (organizationId, subscriptionId, data) => {
  try {
    // Update to match your actual backend route structure
    const response = await api.post(
      `/subscription/organizations/${organizationId}/seats/add`,  // Changed route path 
      {
        ...data,
        subscription_id: subscriptionId,  // Include this in the body instead
        idempotencyKey: `seat_add_${Date.now()}_${subscriptionId}`
      }
    );
    return response;
  } catch (error) {
    console.error('Failed to add seats with payment:', error);
    throw error;
  }
},
  
  // Get whether the organization has a payment method
  getPaymentMethods: async (organizationId) => {
    try {
      const response = await api.get(`/subscription/organizations/${organizationId}/payment-methods`);
      return response;
    } catch (error) {
      console.error('Failed to get payment methods:', error);
      throw error;
    }
  }
};

export default api;

// Add this logging to the api.js file
const get = async (url, config = {}) => {
  console.log(`API GET request to ${url}`);
  try {
    const response = await api.get(url, config);
    console.log(`API GET response from ${url}:`, response);
    return response;
  } catch (error) {
    console.error(`API GET error from ${url}:`, error);
    throw error;
  }
};